<template>
  <div class="hms-page">
    <Navigation />

    <div class="container hms-page-body">
      <div class="d-flex align-items-center justify-content-between mb-4 hms-page-header">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/profile')" class="hms-back" title="Back">
            <i class="mdi mdi-arrow-left"></i>
          </a>
          <h4 class="hms-page-title m-0"><i class="mdi mdi-shield-lock me-2"></i>Security &amp; MFA</h4>
        </div>
        <button class="hms-btn hms-btn-outline" @click="$router.push('/profile')">
          <i class="mdi mdi-account-circle me-1"></i>My Profile
        </button>
      </div>

      <div v-if="loading" class="hms-spinner"><div class="spinner-border"></div></div>

      <!-- ===== CURRENT STATE ===== -->
      <div v-else class="hms-card mb-3">
        <div class="d-flex align-items-center justify-content-between">
          <div>
            <div class="hms-h5 mb-1">Two-Factor Authentication (TOTP)</div>
            <div class="hms-muted-sm">
              A rotating 6-digit code from your authenticator app is required alongside your password.
              Compliant with NIST SP 800-63B AAL-2.
            </div>
            <div class="mt-2">
              <span v-if="status.enabled" class="hms-badge hms-badge-success">
                <i class="mdi mdi-shield-check me-1"></i> Enabled
              </span>
              <span v-else class="hms-badge hms-badge-warning">
                <i class="mdi mdi-shield-off-outline me-1"></i> Not enabled
              </span>
              <span v-if="status.enabled && status.enrolled_at" class="hms-muted-sm ms-2">
                since {{ new Date(status.enrolled_at).toLocaleString() }}
              </span>
            </div>
          </div>
          <div>
            <button v-if="!status.enabled && !enrolment"
                    class="hms-btn hms-btn-blue" @click="startEnrol" :disabled="busy">
              <i class="mdi mdi-shield-plus me-1"></i>
              {{ busy ? 'Generating…' : 'Enable MFA' }}
            </button>
            <button v-if="status.enabled"
                    class="hms-btn hms-btn-outline-danger" @click="disable" :disabled="busy">
              <i class="mdi mdi-shield-off me-1"></i>
              {{ busy ? 'Disabling…' : 'Disable MFA' }}
            </button>
          </div>
        </div>
      </div>

      <!-- ===== ENROLMENT (shown after 'Enable MFA') ===== -->
      <div v-if="enrolment" class="hms-card mb-3">
        <div class="hms-h5 mb-2">Step 1 — Scan this QR in your authenticator app</div>
        <div class="hms-muted-sm mb-3">
          Google Authenticator, Authy, 1Password, Microsoft Authenticator — any RFC 6238 app.
          If you can't scan, tap "Enter setup key" and paste the code below.
        </div>

        <div class="row g-3 align-items-start">
          <div class="col-md-4 text-center">
            <img :src="enrolment.qr_url" alt="MFA QR code"
                 style="width:220px;height:220px;border:1px solid #eee;padding:8px;background:#fff;border-radius:8px;" />
          </div>
          <div class="col-md-8">
            <label class="hms-label">Setup key (Base32)</label>
            <div class="input-group mb-3">
              <input class="hms-input hms-mono" :value="enrolment.secret" readonly />
              <button class="hms-btn hms-btn-outline" @click="copy(enrolment.secret)">
                <i class="mdi mdi-content-copy"></i>
              </button>
            </div>

            <label class="hms-label">otpauth URI</label>
            <div class="input-group mb-3">
              <input class="hms-input hms-mono" :value="enrolment.otpauth_uri" readonly style="font-size:11px;" />
              <button class="hms-btn hms-btn-outline" @click="copy(enrolment.otpauth_uri)">
                <i class="mdi mdi-content-copy"></i>
              </button>
            </div>

            <div class="hms-h6 mt-4 mb-2">Backup codes (save these — each usable once)</div>
            <div class="hms-mono" style="background:#f4f6f9;padding:12px;border-radius:6px;font-size:13px;line-height:1.7;">
              <div v-for="c in enrolment.backup_codes" :key="c">{{ c }}</div>
            </div>
            <button class="hms-btn hms-btn-outline mt-2" @click="copy(enrolment.backup_codes.join('\n'))">
              <i class="mdi mdi-content-copy me-1"></i> Copy all
            </button>
          </div>
        </div>

        <hr class="my-4" />

        <div class="hms-h5 mb-2">Step 2 — Enter a code from your authenticator app to activate</div>
        <div class="row g-3 align-items-end">
          <div class="col-md-4">
            <label class="hms-label">6-digit code</label>
            <input v-model="verifyCode" class="hms-input hms-mono"
                   maxlength="6" inputmode="numeric" placeholder="123 456" style="font-size:20px;letter-spacing:6px;" />
          </div>
          <div class="col-md-8">
            <button class="hms-btn hms-btn-success" @click="verify" :disabled="busy || verifyCode.length !== 6">
              <i class="mdi mdi-shield-check me-1"></i>
              {{ busy ? 'Verifying…' : 'Verify & Activate' }}
            </button>
            <button class="hms-btn hms-btn-outline ms-2" @click="cancelEnrol">Cancel</button>
          </div>
        </div>
      </div>

      <!-- ===== MESSAGES ===== -->
      <div v-if="errorMsg" class="alert alert-danger mt-3">
        <i class="mdi mdi-alert-circle me-1"></i> {{ errorMsg }}
      </div>
      <div v-if="successMsg" class="alert alert-success mt-3">
        <i class="mdi mdi-check-circle me-1"></i> {{ successMsg }}
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import Navigation from "@/components/hms-auth/Navigation.vue";
import { executeJson, hms } from "@/api";

export default {
  name: "Security",
  components: { Navigation },
  data() {
    return {
      loading: true,
      busy: false,
      status: { enabled: false, enrolled_at: null },
      enrolment: null,
      verifyCode: "",
      errorMsg: "",
      successMsg: ""
    };
  },
  async mounted() {
    await this.refreshStatus();
    this.loading = false;
  },
  methods: {
    async refreshStatus() {
      this.errorMsg = "";
      try {
        const { data } = await executeJson({ function: "getMfaStatus" }, hms + "?function=getMfaStatus");
        if (data.success && data.data) {
          this.status.enabled = !!(data.data.is_enabled || data.data.enabled);
          this.status.enrolled_at = data.data.enrolled_at || null;
        }
      } catch (e) {
        this.errorMsg = "Could not load MFA status: " + (e.response?.data?.message || e.message);
      }
    },
    async startEnrol() {
      this.busy = true; this.errorMsg = ""; this.successMsg = "";
      try {
        const { data } = await executeJson({ function: "enrolMfa" }, hms + "?function=enrolMfa");
        if (data.success && data.data) {
          this.enrolment = data.data;
        } else {
          this.errorMsg = data.message || "Could not start enrolment.";
        }
      } catch (e) {
        this.errorMsg = "Enrolment failed: " + (e.response?.data?.message || e.message);
      } finally {
        this.busy = false;
      }
    },
    async verify() {
      this.busy = true; this.errorMsg = "";
      try {
        const { data } = await executeJson(
          { function: "verifyMfa", code: this.verifyCode.trim() },
          hms + "?function=verifyMfa"
        );
        if (data.success) {
          this.successMsg = "MFA activated. Future logins will require your authenticator code.";
          this.enrolment = null;
          this.verifyCode = "";
          await this.refreshStatus();
        } else {
          this.errorMsg = data.message || "Verification failed.";
        }
      } catch (e) {
        this.errorMsg = "Verify failed: " + (e.response?.data?.message || e.message);
      } finally {
        this.busy = false;
      }
    },
    async disable() {
      if (!confirm("Disable MFA on your account? Future logins will only require your password.")) return;
      this.busy = true; this.errorMsg = "";
      try {
        const { data } = await executeJson({ function: "disableMfa" }, hms + "?function=disableMfa");
        if (data.success) {
          this.successMsg = "MFA disabled.";
          await this.refreshStatus();
        } else {
          this.errorMsg = data.message || "Could not disable MFA.";
        }
      } catch (e) {
        this.errorMsg = "Disable failed: " + (e.response?.data?.message || e.message);
      } finally {
        this.busy = false;
      }
    },
    cancelEnrol() {
      this.enrolment = null;
      this.verifyCode = "";
      this.errorMsg = "";
    },
    async copy(text) {
      try { await navigator.clipboard.writeText(text); this.successMsg = "Copied."; setTimeout(()=>this.successMsg="",1500); }
      catch { /* ignore */ }
    }
  }
};
</script>
